/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import {
  PostApply_LeadResendEmailOperation,
  UpdateUserOperation,
  RewardProduct_Detail,
} from '__generated__/graphql';
import classNames from 'classnames';
import { MaterialIcon } from 'components/modulab/MaterialIcon';
import { useMutation } from 'hooks/useQuery';
import { usePathname } from 'next/navigation';
import {
  FormEvent,
  FormEventHandler,
  JSX,
  useState,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { t } from 'utils/locale';
import { trackLeadCreation } from 'utils/serverTracking';
import { Translated } from 'utils/staticText';
import {
  trackingAuthButtonClick,
  trackingLeadCaptureAuthButton,
  trackLeadCaptureFormInteract,
  trackingMembershipRewards,
  MembershipRewardsTrackingData,
  normalizePhoneNumber,
} from 'utils/tracking';
import { RewardStickyFooterText } from './i18n';
import { isInsuranceProduct } from './insuranceVerticals';
import {
  createLead,
  SendLeadToServiceBus,
} from '$/(application)/apply/[productId]/server-actions';
import AccountUser from '$/components/icons/AccountUser';
import GoogleLogo from '$/components/icons/GoogleLogo';
import InfoCircle from '$/components/icons/InfoCircle';
import Mail from '$/components/icons/Mail';
import { LoaderForm, SubmitButton } from '$/components/LoaderForm';
import { StickyFooter } from '$/components/StickyFooter';
import { useGoogleLogin, useIdentity } from '$/modules/auth/hooks';
import { AccessClaims, IdentityClaims } from '$/modules/auth/shared';
import { useClientContext } from '$/modules/context/client';
import { parseForm } from '$/modules/form';
import AlphaCheckbox from '$/modules/material-tailwind/AlphaCheckbox';
import { AlphaTextInput } from '$/modules/material-tailwind/AlphaTextInput';

type Committed = {
  email: string;
  leadId?: number;
  contactNumber?: string;
};

type StickyFooterProps = {
  staticText: Translated<typeof RewardStickyFooterText>;
  productId?: number;
  authEndpoint: string;
  redirectUrl: string;
  postGoogleAuthRedirectUrl: string;
  showContactNumberOnLeadCapture?: boolean;
  requiredContactNumberOnLeadCapture?: boolean;
  product: RewardProduct_Detail;
  trackLeadCreation: typeof trackLeadCreation;
  variant: 'VARIANT_A' | 'VARIANT_B';
  userPhoneNumber?: string;
  tenant?: string;
  currentUser?: AccessClaims;
};

export function RewardStickyFooter(props: StickyFooterProps): JSX.Element {
  const identity = useIdentity();
  const [committed, setCommitted] = useState<Committed | null>(null);
  const [showInputForm, setShowInputForm] = useState<boolean>(
    getSubmissionMethod(props.currentUser) === 'google' ? true : false,
  );
  const [showBackupDialog, setShowBackupDialog] = useState<boolean>(false);
  const [isInAppBrowser, setIsInAppBrowser] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState<boolean>(true);
  const [isMarketingConsentExpanded, setIsMarketingConsentExpanded] =
    useState(false);
  const redirectUrl =
    typeof window !== 'undefined'
      ? `${props.redirectUrl}${location?.search}`
      : props.redirectUrl;

  useEffect(() => {
    const userAgent = navigator.userAgent || '';
    const isFacebookApp =
      userAgent.includes('FBAN') || userAgent.includes('FBAV');
    const isInstagramApp = userAgent.includes('Instagram');

    setIsInAppBrowser(isFacebookApp || isInstagramApp);
  }, []);

  const resendEmailMutation = useMutation(PostApply_LeadResendEmailOperation);
  const googleLogin = useGoogleLogin(props.authEndpoint, undefined, true);
  const updateUser = useMutation(UpdateUserOperation);
  const pathname = usePathname();
  const getGoogleRedirectUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const urlParams = new URLSearchParams(location.search ?? '');
    if (
      props.requiredContactNumberOnLeadCapture &&
      props.showContactNumberOnLeadCapture
    ) {
      urlParams.set('action', 'google');
      return `${pathname}?${urlParams.toString()}`;
    } else {
      urlParams.set('consent', marketingConsent.toString());
      return `${props.postGoogleAuthRedirectUrl}?${urlParams.toString()}`;
    }
  }, [
    props.requiredContactNumberOnLeadCapture,
    props.showContactNumberOnLeadCapture,
    props.postGoogleAuthRedirectUrl,
    pathname,
    marketingConsent,
  ]);

  const tracking: MembershipRewardsTrackingData = {
    campaignId: props.product.productId,
    campaignName: props.product.productDisplayName,
    product: {
      productUid: props.product?.catalogueProduct?.productUid,
      productName: props.product?.catalogueProduct?.productName,
      providerName: props.product?.catalogueProduct?.provider?.providerName,
    },
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    const entries = parseForm(event.currentTarget);
    const email = identity?.email ?? String(entries.email);
    const contactNumber = entries.phoneNumber
      ? String(entries.phoneNumber)
      : undefined;

    if (!props.productId || !email) return;

    const requestSource = ['MEMBERSHIP-V2', props.variant];

    const experimentId = getMetaData('experiment_id');
    const variantId = getMetaData('variant_id');
    const submissionMethod = getSubmissionMethod(props.currentUser);
    if (experimentId) requestSource.push(`exp-${experimentId}`);
    if (variantId) requestSource.push(`var-${variantId}`);
    if (submissionMethod) requestSource.push(`met-${submissionMethod}`);

    trackingMembershipRewards(
      'Confirm Lead Details',
      'lead-submission',
      1,
      {
        ...tracking,
        email,
        contactNumber: normalizePhoneNumber(contactNumber, props.tenant),
      },
      'bottom-sticky',
      'CTA_Confirm Details',
    );

    if (!identity) {
      window.open(redirectUrl);
    }

    const result = await createLead({
      email,
      contact: contactNumber,
      productId: props.productId,
      consent: Boolean(entries.marketingConsent),
      searchParams: window.location.search,
      requestSource: requestSource.join(','),
    });

    await SendLeadToServiceBus({
      External_Lead_Id: result.leadId.toString(),
      Email: email,
      Last_Name: email ?? '-', // Last name is required, so we use "-" as a placeholder
      Product_Type: props?.product?.productVertical?.name ?? '',
      Mobile: contactNumber ?? '',
      Marketing_Consent: Boolean(entries.marketingConsent),
      Selected_Product: props.product?.catalogueProduct?.productName ?? '',
      Selected_Provider:
        props.product?.catalogueProduct?.provider?.providerName ?? '',
      Card_Application_Link: result.redemptionUrl,
    });

    if (props.product.productId) {
      trackLeadCreation({
        email: email,
        vertical: props.product.productVertical?.code ?? '',
        item_brand:
          props?.product.catalogueProduct?.provider?.providerName ?? '',
        item_id: props.product.catalogueProduct?.productUid ?? '',
        item_name: props.product.catalogueProduct?.productName ?? '',
        reward_lead_id: result.leadId,
        reward_campaign_name: props.product.productName ?? '',
        reward_campaign_id: props.product.productId,
        login_method: submissionMethod,
      });
    }

    // if user is logged in, redirect current page to the provider page
    // otherwise show the backup dialog and open the provider page on the new tab
    if (identity) {
      if (entries.phoneNumber) {
        await updateUser.send({
          user: {
            phoneNumber: String(entries.phoneNumber),
          },
        });
      }
      window.location.href = redirectUrl;
      return;
    }

    setCommitted({
      leadId: result.leadId,
      email,
      contactNumber,
    });
    setShowInputForm(false);
    setShowBackupDialog(true);
  };

  const onResendEmail = async (event: FormEvent<HTMLFormElement>) => {
    const entries = parseForm(event.currentTarget);
    const email = identity?.email ?? String(entries.email);

    if (!props.productId || !email || committed?.leadId == null) return;
    if (committed?.email === email && committed.leadId != null) {
      await resendEmailMutation.send({
        input: {
          email,
          type: 'EMAIL_SUBMISSION',
          leadId: committed.leadId,
        },
      });
      trackingMembershipRewards(
        'Resend Email',
        'start-of-application',
        2,
        tracking,
      );
    }
  };

  // Insurance rewards are mostly lucky-draw entries, so the heading and the
  // login CTAs are worded differently from the banking ones. Anything that is
  // not insurance keeps today's copy.
  const isInsurance = isInsuranceProduct(props.product);

  return (
    <StickyFooter>
      <div className="mhg-alpha-box-shadow-2 px-[--spacing-padding-x] py-[--spacing-padding-y] bg-[--fill-card]">
        <div className="mx-auto flex flex-col items-center justify-between gap-[--spacing-gap-y-sm] md:max-w-[--general-narrow-content-max-width-2] lg:max-w-[--general-narrow-content-max-width-3] lg:flex-row lg:gap-[--spacing-gap-x-sm]">
          <div
            className={classNames(
              'mhg-alpha-title-1 text-[--text-primary] lg:pr-10 xl:pr-20',
              // The insurance heading is a full sentence, so it has to be able
              // to wrap next to the buttons on desktop.
              { 'shrink-0': !isInsurance },
            )}
          >
            {t(
              isInsurance
                ? props.staticText.contentINS
                : props.staticText.content,
              {
                verticalName:
                  props.product?.productVertical?.name
                    ?.replace(/Unsecured\s+/i, '')
                    ?.replace(/Secured\s+/i, '')
                    ?.replace(/s$/, '') ?? 'Application',
              },
            )}
          </div>
          {identity && (
            <LoaderForm
              className="flex w-full flex-col gap-[10px]"
              onSubmit={onSubmit}
            >
              <button
                className={classNames(
                  'mhg-alpha-subheading-2 md:mhg-alpha-button-1 flex w-full items-center justify-center gap-[--button-gap] rounded-[--radius-button] pl-[--button-padding-regular-x-icon] pr-[--button-padding-regular-x] bg-[--buttons-primary] text-[--text-on-dark] md:min-w-[--general-4-col-width] lg:min-w-[--general-3-col-width]',
                  isInsurance
                    ? // The insurance label plus an email does not fit on one line at
                      // 320px: the button grows to a second line and the email
                      // ellipsizes, so the label itself is never clipped.
                      'min-h-[--button-size-regular] py-[--spacing-padding-y-xs]'
                    : 'h-[--button-size-regular] py-0',
                )}
                type={
                  props.showContactNumberOnLeadCapture ? 'button' : 'submit'
                }
                onClick={() => {
                  if (props.showContactNumberOnLeadCapture)
                    setShowInputForm(true);
                  trackingLeadCaptureAuthButton(
                    'Continue with Selected Account',
                    getSubmissionMethod(identity),
                    'bottom-sticky',
                    `CTA_Continue with Selected Account`,
                    tracking,
                    marketingConsent,
                    'lead-submission',
                    'reward_gateway',
                  );
                }}
              >
                <span>
                  <AccountUser className="size-[--shapes-icon-size-lg]" />
                </span>
                <div
                  className={classNames('min-w-0', { truncate: !isInsurance })}
                >
                  {t(
                    isInsurance
                      ? props.staticText.continueAsBtnINS
                      : props.staticText.continueAsBtn,
                    {
                      userEmail: (
                        <span className="inline-block max-w-full truncate align-bottom">
                          {identity.email}
                        </span>
                      ),
                    },
                  )}
                </div>
              </button>

              <MarketingConsentCheckbox
                isExpanded={isMarketingConsentExpanded}
                marketingConsent={marketingConsent}
                product={props.product}
                setIsExpanded={setIsMarketingConsentExpanded}
                setMarketingConsent={setMarketingConsent}
                staticText={props.staticText}
              />
            </LoaderForm>
          )}

          {!identity && (
            <LoaderForm
              className="flex w-full flex-col gap-[10px]"
              onSubmit={onSubmit}
            >
              <div className="flex shrink flex-col-reverse items-center gap-[--spacing-gap-y-sm] md:flex-row md:gap-[--spacing-gap-x-sm] lg:w-auto">
                <button
                  className="mhg-alpha-subheading-2 md:mhg-alpha-button-1 flex h-[--button-size-regular] w-full items-center justify-center gap-[--button-gap] rounded-[--radius-button] py-0 pl-[--button-padding-regular-x-icon] pr-[--button-padding-regular-x] bg-[--buttons-primary] text-[--text-on-dark] lg:w-full lg:min-w-[--general-3-col-width]"
                  type="button"
                  onClick={() => {
                    setShowInputForm(true);
                    trackingLeadCaptureAuthButton(
                      'Continue with Email',
                      'email',
                      'bottom-sticky',
                      `CTA_Continue with Email`,
                      tracking,
                      false,
                      'lead-submission',
                      'reward_gateway',
                    );
                  }}
                >
                  <Mail className="size-[--shapes-icon-size-lg]" />
                  {isInsurance
                    ? props.staticText.continueWithEmailINS
                    : props.staticText.continueWithEmail}
                </button>
                {!isInAppBrowser && (
                  <button
                    className="mhg-alpha-subheading-2 md:mhg-alpha-button-1 flex h-[--button-size-regular] w-full items-center justify-center gap-[--button-gap] rounded-[--radius-button] py-0 pl-[--button-padding-regular-x-icon] pr-[--button-padding-regular-x] bg-[--buttons-neutral-dark] text-[--text-on-dark] lg:w-full lg:min-w-[--general-3-col-width]"
                    type="button"
                    onClick={(e) => {
                      trackingLeadCaptureAuthButton(
                        'Continue with Google',
                        'google',
                        'bottom-sticky',
                        `CTA_Continue with Google`,
                        tracking,
                        false,
                        'lead-submission',
                        'reward_gateway',
                      );
                      trackingAuthButtonClick(
                        'Continue with Google',
                        'google',
                        `CTA_Continue with Google`,
                        false,
                        'bottom-sticky',
                        'lead-submission',
                        undefined,
                        'rewards page',
                      );
                      googleLogin(e);
                    }}
                  >
                    <GoogleLogo className="size-[--shapes-icon-size-lg]" />
                    {props.staticText.continueWithGoogle}
                  </button>
                )}
              </div>
              <MarketingConsentCheckbox
                isExpanded={isMarketingConsentExpanded}
                marketingConsent={marketingConsent}
                product={props.product}
                setIsExpanded={setIsMarketingConsentExpanded}
                setMarketingConsent={setMarketingConsent}
                staticText={props.staticText}
              />
              <input
                type="hidden"
                name="redirect_uri"
                value={getGoogleRedirectUrl}
              />
            </LoaderForm>
          )}
        </div>
      </div>
      <InputDialog
        staticText={props.staticText}
        identity={identity}
        isOpen={showInputForm}
        setOpen={setShowInputForm}
        onSubmit={onSubmit}
        showContactNumberOnLeadCapture={props.showContactNumberOnLeadCapture}
        requiredContactNumberOnLeadCapture={
          props.requiredContactNumberOnLeadCapture
        }
        productUid={props.product.catalogueProduct?.productUid ?? ''}
        userPhoneNumber={props.userPhoneNumber}
        tracking={tracking}
        marketingConsent={marketingConsent}
      />
      {committed && (
        <BackupPlanDialog
          isOpen={showBackupDialog}
          setOpen={setShowBackupDialog}
          staticText={props.staticText}
          providerUri={redirectUrl}
          onSubmit={onResendEmail}
          tracking={tracking}
          committed={committed}
          tenant={props.tenant}
        />
      )}
    </StickyFooter>
  );
}

function MarketingConsentCheckbox(props: {
  isExpanded: boolean;
  marketingConsent: boolean;
  product: RewardProduct_Detail;
  setIsExpanded: (value: boolean) => void;
  setMarketingConsent: (value: boolean) => void;
  staticText: Translated<typeof RewardStickyFooterText>;
}) {
  const handleLabelClick = (e: React.MouseEvent<HTMLDivElement>) => {
    let target = e.target as HTMLElement;
    // If the target is a link, stop the propagation
    while (target && target !== e.currentTarget) {
      if (target.tagName.toLowerCase() === 'a') {
        return;
      }
      target = target.parentElement as HTMLElement;
    }

    e.stopPropagation();
    props.setMarketingConsent(!props.marketingConsent);
    trackLeadCaptureFormInteract(
      'checkbox',
      'Lead Details_Marketing Consent',
      '<<PII redacted>>',
      'lead-submission',
      1,
      props.product.catalogueProduct?.productUid ?? '',
      !props.marketingConsent ? 'check' : 'uncheck',
    );
  };

  return (
    <>
      <div className="flex items-start gap-2">
        <AlphaCheckbox
          checked={props.marketingConsent}
          onChange={() => {
            props.setMarketingConsent(!props.marketingConsent);
            trackLeadCaptureFormInteract(
              'checkbox',
              'Lead Details_Marketing Consent',
              '<<PII redacted>>',
              'lead-submission',
              1,
              props.product.catalogueProduct?.productUid ?? '',
              !props.marketingConsent ? 'check' : 'uncheck',
            );
          }}
          labelProps={{
            htmlFor: 'marketingConsent',
            title: 'Marketing Consent',
          }}
          inputProps={{
            id: 'marketingConsent',
            name: 'marketingConsent',
            'aria-describedby': 'marketingConsent-desc',
            value: '1',
          }}
        />
        {/* Note: This is a workaround to prevent the checkbox from being checked when see more is clicked */}
        <div
          id="marketingConsent-desc"
          className="mhg-alpha-body-2 cursor-pointer text-[--text-scondary]"
          onClick={handleLabelClick}
          role="button"
          tabIndex={0}
          aria-label="Marketing consent text"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              props.setMarketingConsent(!props.marketingConsent);
              trackLeadCaptureFormInteract(
                'checkbox',
                'Lead Details_Marketing Consent',
                '<<PII redacted>>',
                'lead-submission',
                1,
                props.product.catalogueProduct?.productUid ?? '',
                !props.marketingConsent ? 'check' : 'uncheck',
              );
            }
          }}
        >
          {props.isExpanded ? (
            props.staticText.dialogMarketingConsent
          ) : (
            <SeeMoreMarketingConsent
              shortContent={props.staticText.dialogMarketingConsentShort}
              seeMoreText={props.staticText.dialogSeeMore}
              setIsExpanded={props.setIsExpanded}
            />
          )}
        </div>
      </div>
      <div className="mhg-alpha-body-2 -mt-1 text-[--text-scondary]">
        {props.staticText.dialogTermsAndConditions}
      </div>
    </>
  );
}

function SeeMoreMarketingConsent(props: {
  shortContent: string;
  seeMoreText: string;
  setIsExpanded: (value: boolean) => void;
}) {
  return (
    <>
      {`${props.shortContent}... `}
      <button
        className="underline"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          props.setIsExpanded(true);
        }}
      >
        {props.seeMoreText}
      </button>
    </>
  );
}

function InputDialog(props: {
  staticText: Translated<typeof RewardStickyFooterText>;
  isOpen: boolean;
  setOpen: (value: boolean) => void;
  onSubmit: FormEventHandler<HTMLFormElement>;
  identity?: IdentityClaims;
  showContactNumberOnLeadCapture?: boolean;
  requiredContactNumberOnLeadCapture?: boolean;
  productUid: string;
  userPhoneNumber?: string;
  tracking: MembershipRewardsTrackingData;
  marketingConsent: boolean;
}): JSX.Element {
  const [isSubmitbuttonEnable, setSubmitbuttonEnable] =
    useState<boolean>(false);
  const [emailField, setEmailField] = useState<string | undefined>(
    props.identity?.email,
  );
  const [phoneNumberField, setPhoneNumberField] = useState<string | undefined>(
    props.userPhoneNumber,
  );
  const { authEndpoint } = useClientContext();
  const logout = () => {
    trackingLeadCaptureAuthButton(
      'Logout CTA',
      '',
      'text-link',
      'Lead Details_Logout',
      props.tracking,
      props.marketingConsent,
      'lead-submission',
      'reward_gateway',
    );
    trackingAuthButtonClick(
      'Logout CTA',
      '',
      `Lead Details_Logout`,
      props.marketingConsent,
      'text-link',
      'lead-submission',
      undefined,
      'rewards page',
    );
    const url = new URL(`${authEndpoint}/logout`, window.location.href);
    fetch(url, { method: 'POST', credentials: 'include' })
      .then((response) => {
        if (response.ok) {
          window.location.reload();
        } else {
          console.error('Logout failed:', response.statusText);
        }
      })
      .catch((error) => {
        console.error('Error during logout:', error.message);
      });
  };

  const modalRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    function clickOutside(e: MouseEvent) {
      if (
        props.isOpen &&
        modalRef.current &&
        !modalRef.current.contains(e.target as Node)
      ) {
        props.setOpen(false);
        trackingLeadCaptureAuthButton(
          'Quit Lead Details',
          getSubmissionMethod(props.identity),
          'modal-outside',
          'Modal Outside',
          props.tracking,
          props.marketingConsent,
          'lead-submission',
          'reward_gateway',
        );
      }
    }

    window.addEventListener('mousedown', clickOutside);
    return () => {
      window.removeEventListener('mousedown', clickOutside);
    };
  }, [props]);

  const showPhoneNumberField = props.showContactNumberOnLeadCapture;
  useEffect(() => {
    if (
      showPhoneNumberField &&
      props.requiredContactNumberOnLeadCapture &&
      !phoneNumberField
    ) {
      setSubmitbuttonEnable(false);
    } else if (!emailField) {
      setSubmitbuttonEnable(false);
    } else {
      setSubmitbuttonEnable(true);
    }
  }, [
    showPhoneNumberField,
    phoneNumberField,
    emailField,
    props.requiredContactNumberOnLeadCapture,
  ]);

  return (
    <div
      className="fixed bottom-0 left-0"
      style={{ display: props.isOpen ? 'block' : 'none' }}
    >
      <div className="relative grid h-screen w-screen place-items-center p-4 bg-black/50">
        <div
          ref={modalRef}
          className="mhg-alpha-box-shadow-2 fixed bottom-0 left-0 flex w-full justify-center px-[--spacing-padding-x] py-[--spacing-padding-y] bg-[--fill-card]"
        >
          <LoaderForm
            onSubmit={props.onSubmit}
            className="flex w-90 flex-col items-start justify-between gap-[--spacing-gap-y] font-[--font-font] md:w-[--general-narrow-content-max-width-3]"
          >
            {props.marketingConsent && (
              <input type="hidden" name="marketingConsent" value="true" />
            )}
            <div className="mhg-alpha-title-1 text-[--text-primary]">
              {showPhoneNumberField
                ? props.staticText.dialogTitleWithPhoneNumberField
                : props.staticText.dialogTitle}
            </div>
            <div className="flex w-full flex-col items-start gap-[--spacing-gap-y-sm] self-stretch md:flex-row md:gap-[--spacing-gap-x]">
              {showPhoneNumberField && (
                <div className="flex w-full flex-col gap-1">
                  <div className="flex flex-col items-start self-stretch">
                    <div className="mhg-alpha-form-label flex gap-1">
                      <div>{props.staticText.fieldPhoneNumber.title}</div>
                      <div className="group relative">
                        <InfoCircle className="size-[--shapes-icon-size-md] text-[color:--colors-neutral-gray-700] text-[length:--shapes-icon-size-md]" />
                        <div className="mhg-alpha-caption absolute bottom-full left-1/2 mb-2 hidden w-max max-w-50 -translate-x-1/2 rounded-lg p-3 opacity-0 shadow transition-opacity duration-200 bg-white text-[--text-primary] text-sm group-hover:block group-hover:opacity-100 md:max-w-75">
                          {props.staticText.fieldPhoneNumber.tooltip}
                        </div>
                      </div>
                    </div>
                  </div>
                  <AlphaTextInput
                    // eslint-disable-next-line tailwindcss/no-custom-classname -- replace material-tailwind class
                    className="!mhg-alpha-form-input flex items-center self-stretch [appearance:textfield] read-only:bg-[--highlights-gray-light] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    type="number"
                    name="phoneNumber"
                    placeholder={props.staticText.fieldPhoneNumber.placeholder}
                    pattern={props.staticText.fieldPhoneNumber.pattern}
                    onValidate={(state) => {
                      if (
                        (state.validity.patternMismatch &&
                          state.value !== '') ||
                        (state.validity.valueMissing &&
                          props.requiredContactNumberOnLeadCapture)
                      ) {
                        return props.staticText.fieldPhoneNumber.invalid;
                      }
                    }}
                    defaultValue={phoneNumberField}
                    required={props.requiredContactNumberOnLeadCapture ?? false}
                    onChange={(e) => {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      clearTimeout((e.target as any).delayTimeout);
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (e.target as any).delayTimeout = setTimeout(() => {
                        trackLeadCaptureFormInteract(
                          'textbox',
                          'Lead Detail_Phone Number',
                          '<<PII redacted>>',
                          'lead-submission',
                          1,
                          props.productUid.split('.')[1] ?? '',
                        );
                      }, 300);
                      setPhoneNumberField(e.target.value);
                    }}
                  />
                </div>
              )}
              {(!props.identity?.email || showPhoneNumberField) && (
                <div className="flex w-full flex-col gap-1">
                  <div className="flex flex-row justify-between">
                    <div className="mhg-alpha-form-label">
                      {props.staticText.fieldEmail.title}
                    </div>
                    {props.identity?.email && (
                      <button
                        type="button"
                        className="mhg-alpha-form-label cursor-pointer underline text-[--text-link]"
                        onClick={logout}
                      >
                        {props.staticText.logoutBtn}
                      </button>
                    )}
                  </div>
                  <AlphaTextInput
                    // eslint-disable-next-line tailwindcss/no-custom-classname -- replace material-tailwind class
                    className="!mhg-alpha-form-input flex items-center self-stretch !text-ellipsis"
                    type="email"
                    name="email"
                    placeholder={props.staticText.fieldEmail.placeholder}
                    value={props.identity?.email}
                    readOnly={props.identity?.email ? true : false}
                    pattern={props.staticText.fieldEmail.pattern}
                    onValidate={(state) => {
                      if (!state.validity.valid) {
                        return props.staticText.fieldEmail.invalid;
                      }
                    }}
                    required
                    onChange={(e) => {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      clearTimeout((e.target as any).delayTimeout);
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (e.target as any).delayTimeout = setTimeout(() => {
                        trackLeadCaptureFormInteract(
                          'textbox',
                          'Lead Detail_Email',
                          '<<PII redacted>>',
                          'lead-submission',
                          1,
                          props.productUid.split('.')[1] ?? '',
                        );
                      }, 300);
                      setEmailField(e.target.value);
                    }}
                  />
                </div>
              )}
              {(!props.identity?.email || showPhoneNumberField) && (
                <div className="flex w-full flex-col items-center gap-1 md:items-start">
                  <SubmitButton
                    type="button"
                    className="mhg-alpha-btn-primary mt-auto w-full gap-[--button-gap] !rounded-[--radius-button] disabled:bg-[--buttons-disable] md:mt-[26px] md:w-[--general-3-col-width] lg:mt-7"
                    disabled={!isSubmitbuttonEnable}
                  >
                    {props.staticText.dialogSubmitBtn}
                    <MaterialIcon icon="check_circle" size={3} />
                  </SubmitButton>
                </div>
              )}
            </div>
            <div className="flex w-full flex-col items-center justify-start gap-[--spacing-gap-y] self-stretch md:flex-row-reverse">
              {!showPhoneNumberField && props.identity?.email && (
                <SubmitButton
                  type="button"
                  className="mhg-alpha-btn-primary w-full gap-[--button-gap] !rounded-[--radius-button] disabled:bg-[--buttons-disable] md:w-[--general-3-col-width]"
                  disabled={!isSubmitbuttonEnable}
                >
                  {props.staticText.dialogSubmitBtn}
                  <MaterialIcon icon="check_circle" size={3} />
                </SubmitButton>
              )}
            </div>
          </LoaderForm>
        </div>
      </div>
    </div>
  );
}

function BackupPlanDialog(props: {
  staticText: Translated<typeof RewardStickyFooterText>;
  isOpen: boolean;
  setOpen: (value: boolean) => void;
  onSubmit: FormEventHandler<HTMLFormElement>;
  providerUri: string;
  tracking: MembershipRewardsTrackingData;
  committed: Committed;
  tenant?: string;
}) {
  const { staticText, tracking } = props;

  const modalRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    function clickOutside(e: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        props.setOpen(false);
      }
    }
    document.addEventListener('mousedown', clickOutside);
  }, [props]);

  return (
    <div
      className="fixed left-0 top-0 h-screen w-screen"
      style={{ display: props.isOpen ? 'block' : 'none' }}
    >
      <div className="fixed inset-0 flex items-center justify-center bg-black/50">
        <div
          className="mhg-alpha-box-shadow-2 flex flex-col gap-4 rounded-[--radius-card] px-[--spacing-padding-x] py-[--spacing-padding-y] bg-[--fill-card]"
          ref={modalRef}
        >
          <div className="mhg-alpha-title-1 self-center text-gray-900">
            {staticText.didntGetClaimEmail}
          </div>
          <LoaderForm onSubmit={props.onSubmit}>
            <div className="flex flex-col gap-4">
              <div>
                <AlphaTextInput
                  name="email"
                  type="email"
                  required
                  defaultValue={props.committed.email}
                />
              </div>
              <div>
                <SubmitButton color="primary" fullWidth>
                  {staticText.resendEmail}
                </SubmitButton>
              </div>
            </div>
          </LoaderForm>
          <div className="mhg-alpha-body-1 self-center text-gray-700">
            {staticText.or}
          </div>
          <div className="mhg-alpha-title-1 self-center text-gray-900">
            {staticText.didntCompleteApplication}
          </div>
          <div>
            <a
              href={props.providerUri}
              target="_blank"
              rel="noreferrer"
              className="mhg-alpha-btn-primary"
              onClick={() => {
                trackingMembershipRewards(
                  'Restart the Application',
                  'lead-submission',
                  1,
                  {
                    ...tracking,
                    email: props.committed.email,
                    contactNumber: normalizePhoneNumber(
                      props.committed.contactNumber,
                      props.tenant,
                    ),
                    leadId: props.committed.leadId,
                  },
                  'overlay',
                  'CTA_Restart the Application',
                );
              }}
            >
              {staticText.restartApplication}
              <MaterialIcon icon="open_in_new" size={3} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function getSubmissionMethod(
  currentIdentity: AccessClaims | undefined,
): 'email' | 'google' | 'prelogin' {
  if (typeof window === 'undefined') return 'email'; // Ensure it runs only on the client side
  const urlParams = new URLSearchParams(location.search ?? '');
  const action = urlParams.get('action');
  if (action === 'google') return 'google';
  return currentIdentity?.email ? 'prelogin' : 'email';
}

function getMetaData(name: string) {
  const metaTag = document.querySelector(`meta[name="${name}"]`);
  return metaTag ? metaTag.getAttribute('content') : '';
}
